import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

const baseUrl = process.env.REACT_APP_API_URL || "https://api.naturalremediesapp.com/api";

const createRequest = (url) => ({ url });

const isTokenExpired = (tokenExpiry) => {
  if (!tokenExpiry) return true;
  return new Date(tokenExpiry) <= new Date();
};

const refreshToken = async () => {
  const refreshToken = localStorage.getItem("admin_refresh_token");
  if (!refreshToken) {
    throw new Error("No refresh token available");
  }

  try {
    const response = await fetch(`${baseUrl}/auth/admin/refresh?refresh_token=${refreshToken}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (data.success && data.access_token) {
      localStorage.setItem("admin_token", data.access_token);
      localStorage.setItem("admin_refresh_token", data.refresh_token);
      localStorage.setItem("admin_token_expires_at", data.access_token_expires_at);
      localStorage.setItem("admin_refresh_token_expires_at", data.refresh_token_expires_at);
      return data.access_token;
    } else {
      throw new Error("Failed to refresh token");
    }
  } catch (error) {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_refresh_token");
    localStorage.removeItem("admin_token_expires_at");
    localStorage.removeItem("admin_refresh_token_expires_at");
    localStorage.removeItem("admin_data");
    throw error;
  }
};

export const remediesApiService = createApi({
  reducerPath: "remediesApiService",
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers) => {
      const token = localStorage.getItem("admin_token");
      const tokenExpiry = localStorage.getItem("admin_token_expires_at");

      if (token && isTokenExpired(tokenExpiry)) {
        try {
          const newToken = await refreshToken();
          headers.set("Authorization", `Bearer ${newToken}`);
        } catch (error) {
          window.location.href = "/admin/auth/sign-in";
          throw error;
        }
      } else if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }

      return headers;
    },
  }),

  endpoints: (builder) => ({
    // Get all remedies with optional search and pagination
    getRemedies: builder.query({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        
        if (params.search) {
          queryParams.append("search", params.search);
        }
        if (params.page) {
          queryParams.append("page", params.page);
        }
        if (params.per_page) {
          queryParams.append("per_page", params.per_page);
        }
        
        const queryString = queryParams.toString();
        return {
          url: `/remedies${queryString ? `?${queryString}` : ""}`,
        };
      },
    }),

    // Get single remedy by ID
    getRemedy: builder.query({
      query: (id) => createRequest(`/remedies/${id}/dashboard`),
    }),

    // Create remedy
    createRemedy: builder.mutation({
      query: (remedy) => ({
        url: "/remedies",
        method: "POST",
        body: remedy,
      }),
    }),

    // Update remedy
    updateRemedy: builder.mutation({
      query: ({ id, submissionData }) => ({
        url: `/remedies/${id}`,
        method: "PUT",
        body: submissionData,
      }),
    }),

    // Delete remedy
    deleteRemedy: builder.mutation({
      query: (id) => ({
        url: `/remedies/${id}`,
        method: "DELETE",
      }),
    }),

    // AI remedy suggestion endpoint
    getRemedyAiSuggestion: builder.mutation({
      query: (query) => ({
        url: "/remedy-ai",
        method: "POST",
        body: { query },
      }),
    }),
  }),
});

export const {
  useGetRemediesQuery,
  useGetRemedyQuery,
  useCreateRemedyMutation,
  useUpdateRemedyMutation,
  useDeleteRemedyMutation,
  useGetRemedyAiSuggestionMutation, // New export for the AI endpoint
} = remediesApiService;

export { refreshToken, isTokenExpired };